import styled from '@emotion/styled';
import { IconButton, useTheme } from '@mui/material';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { ReactComponent as MoreIcon } from '../../../assets/icons/more-vertical.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { Typography } from '../../../components/Typography/Typography';
import { Deal, SelectItem } from '../../../types';
import { StatusBadge } from '../../../components/StatusBadge/StatusBadge';
import ActionsMenu from '../components/ActionsMenu';
import { DealFormModal } from '../components/DealFormModal/DealFormModal';
import { DealOwnersCell } from '../components/DealOwnersCell';
import { ArchiveDealModal } from '../components/ArchiveDealModal';
import { UnarchiveDealModal } from '../components/UnarchiveDealModal';
import { OperatingPartnersCell } from '../components/OperatingPartnersCell';

const CellWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const columnHelper = createColumnHelper<Deal>();

export const ActionCell = (props: CellContext<Deal, unknown>) => {
  const { colors } = useTheme();
  const deal = props.row.original;
  const isArchived = deal.isActive === false;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editDealOpen, setEditDealOpen] = useState(false);
  const [archiveDealOpen, setArchiveDealOpen] = useState(false);
  const [unarchiveDealOpen, setUnarchiveDealOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleEditDealOpen = useCallback(() => {
    setEditDealOpen((prev) => !prev);
  }, []);

  const toggleArchiveDeal = useCallback(() => {
    setArchiveDealOpen((prev) => !prev);
  }, []);

  const toggleUnarchiveDeal = useCallback(() => {
    setUnarchiveDealOpen((prev) => !prev);
  }, []);

  return (
    <CellWrapper style={{ justifyContent: 'end', paddingRight: '12px' }}>
      <IconButton
        onClick={toggleEditDealOpen}
        size='small'
        aria-label='Edit deal'
        sx={{ svg: { g: { path: { fill: colors.icon.accent } } } }}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        onClick={handleClick}
        size='small'
        sx={{ svg: { g: { path: { fill: colors.icon.accent } } } }}
      >
        <MoreIcon />
      </IconButton>
      <ActionsMenu
        anchorEl={anchorEl}
        isArchived={isArchived}
        handleClose={handleClose}
        toggleArchiveDeal={toggleArchiveDeal}
        toggleUnarchiveDeal={toggleUnarchiveDeal}
      />
      {editDealOpen && (
        <DealFormModal
          addDealOpen={editDealOpen}
          handleAddDealClose={toggleEditDealOpen}
          deal={deal}
        />
      )}
      {archiveDealOpen && (
        <ArchiveDealModal
          deal={deal}
          archiveDealOpen={archiveDealOpen}
          toggleArchiveDeal={toggleArchiveDeal}
        />
      )}
      {unarchiveDealOpen && (
        <UnarchiveDealModal
          deal={deal}
          unarchiveDealOpen={unarchiveDealOpen}
          toggleUnarchiveDeal={toggleUnarchiveDeal}
        />
      )}
    </CellWrapper>
  );
};

export const useDealsTableColumns = () => {
  const { colors } = useTheme();

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.caption}>
            {info.getValue()}
          </Typography>
        ),
        header: () => <TableHeaderCell text='ID' />,
        meta: {
          width: '45px',
          maxWidth: '50px',
          minWidth: '45px',
          tdStyles: { paddingLeft: '16px', borderRight: `none` },
          thStyles: { borderRight: `none`, paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('name', {
        cell: (info) => (
          <Typography variant='h4' color={colors.text.main}>
            {info.getValue()}
          </Typography>
        ),
        header: () => <TableHeaderCell text='Deal' />,
        filterFn: (row, id, filterValue) => {
          const isIssuerNameMatched = (row.original.name ?? '')
            .toLowerCase()
            .includes(filterValue.toLowerCase());

          const isOwnerMatched = !!row.original.dealOwners?.some((owner) => {
            return owner.name.toLowerCase().includes(filterValue.toLowerCase());
          });

          const isOPMatched = !!row.original.operatingPartners?.some((partner) => {
            return partner.name.toLowerCase().includes(filterValue.toLowerCase());
          });

          return isIssuerNameMatched || isOwnerMatched || isOPMatched;
        },
        meta: {
          width: '200px',
          maxWidth: '200px',
          minWidth: '200px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('isActive', {
        cell: (info) => (
          <StatusBadge
            isActive={info.getValue() !== false}
            activeLabel='Active'
            inactiveLabel='Archived'
          />
        ),
        header: () => <TableHeaderCell text='Status' />,
        enableSorting: false,
        meta: {
          width: '140px',
          maxWidth: '140px',
          minWidth: '140px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('dealOwners', {
        cell: (info) => <DealOwnersCell deals={info.row.original.dealOwners} />,
        header: () => <TableHeaderCell text='Deal Owners' />,
        enableSorting: false,
        meta: {
          width: '400px',
          maxWidth: '400px',
          minWidth: '400px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('operatingPartners', {
        cell: (info) => (
          <OperatingPartnersCell operatingPartners={info.row.original.operatingPartners} />
        ),
        header: () => <TableHeaderCell text='Submitters' />,
        enableSorting: false,
        filterFn: (row, id, filterValue: SelectItem[]) => {
          if (!filterValue.length) return true;
          return filterValue.some(
            (user) =>
              user.id === row.original.operatingPartners?.find((op) => op.id === user.id)?.id
          );
        },
        meta: {
          width: '600px',
          maxWidth: '600px',
          minWidth: '600px',
          tdStyles: { borderRight: `none` },
          thStyles: { borderRight: `none` },
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => <ActionCell {...info} />,
        header: () => <TableHeaderCell text='' />,
        meta: {
          width: '84px',
          minWidth: '84px',
          maxWidth: '84px',
          tdStyles: { paddingLeft: '0', borderLeft: `none` },
          thStyles: { borderLeft: `none` },
        },
      }),
    ],
    [colors]
  );

  return columns;
};
